import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import { articleList } from '@/mocks/articles';

const articleImages: Record<string, string> = {
  'article-ucl': 'https://readdy.ai/api/search-image?query=Professional%20soccer%20match%20dramatic%20lighting%20warm%20golden%20tones%20clean%20background%20editorial%20style%20high%20detail%20cinematic%20composition%20champions%20league%20atmosphere&width=1200&height=600&seq=article-detail-ucl&orientation=landscape',
  'article-nba': 'https://readdy.ai/api/search-image?query=Professional%20basketball%20game%20dramatic%20arena%20lighting%20warm%20golden%20tones%20clean%20background%20editorial%20style%20high%20detail%20NBA%20atmosphere&width=1200&height=600&seq=article-detail-nba&orientation=landscape',
  'article-lck': 'https://readdy.ai/api/search-image?query=Professional%20esports%20arena%20dramatic%20stage%20lighting%20warm%20golden%20tones%20gaming%20competition%20editorial%20style%20high%20detail%20cinematic%20composition&width=1200&height=600&seq=article-detail-lck&orientation=landscape',
  'article-tennis': 'https://readdy.ai/api/search-image?query=Professional%20tennis%20match%20dramatic%20court%20lighting%20warm%20golden%20tones%20clean%20background%20editorial%20style%20high%20detail%20grand%20slam%20atmosphere&width=1200&height=600&seq=article-detail-tennis&orientation=landscape',
  'article-guide': 'https://readdy.ai/api/search-image?query=Modern%20clean%20sports%20platform%20interface%20warm%20lighting%20professional%20setting%20editorial%20style%20high%20detail%20minimalist%20design&width=1200&height=600&seq=article-detail-guide&orientation=landscape',
  'article-faq': 'https://readdy.ai/api/search-image?query=Modern%20customer%20service%20center%20warm%20lighting%20professional%20setting%20clean%20background%20editorial%20style%20high%20detail&width=1200&height=600&seq=article-detail-faq&orientation=landscape',
  'article-deposit': 'https://readdy.ai/api/search-image?query=Modern%20digital%20payment%20interface%20warm%20lighting%20professional%20setting%20clean%20background%20editorial%20style%20high%20detail&width=1200&height=600&seq=article-detail-deposit&orientation=landscape',
  'article-rules': 'https://readdy.ai/api/search-image?query=Professional%20sports%20governance%20documents%20warm%20lighting%20clean%20background%20editorial%20style%20high%20detail%20official%20setting&width=1200&height=600&seq=article-detail-rules&orientation=landscape',
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? articles[id] : undefined;

  if (!article) {
    return (
      <div className="pt-20 pb-14 bg-background-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
            <i className="ri-file-unknow-line text-2xl"></i>
          </div>
          <h2 className="text-xl font-semibold text-foreground-900 mb-2">文章未找到</h2>
          <p className="text-foreground-500 text-sm mb-4">您访问的文章不存在或已被删除</p>
          <Link
            to="/category/news"
            className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
          >
            返回文章列表
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articleList
    .filter((a) => a.id !== article.id && (a.categorySlug === article.categorySlug || a.category === article.category))
    .slice(0, 3);

  return (
    <>
      <SEOHead
        title={`${article.title} - 开元体育`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://www.kaiyuansports.com/article/${article.id}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogType="article"
        articleDate={article.date}
        articleTags={article.tags}
      />

      <div className="pt-20 pb-14 md:pb-20 bg-background-50">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { name: '首页', href: '/' },
                { name: '体育资讯', href: '/category/news' },
                { name: article.category, href: `/category/${article.categorySlug}` },
                { name: article.title },
              ]}
            />

            <article className="mt-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <span className="px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                    {article.category}
                  </span>
                  <span className="text-xs text-foreground-400">
                    <i className="ri-calendar-line mr-1"></i>{article.date}
                  </span>
                  <span className="text-xs text-foreground-400">
                    <i className="ri-time-line mr-1"></i>{article.readTime}
                  </span>
                  <span className="text-xs text-foreground-400">
                    <i className="ri-user-line mr-1"></i>{article.author}
                  </span>
                </div>

                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                  {article.title}
                </h1>

                <div className="flex items-center gap-2 flex-wrap">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-0.5 rounded-full bg-background-100 text-foreground-500 text-xs"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="relative h-64 md:h-80 rounded-lg overflow-hidden mb-8">
                <img
                  src={articleImages[article.imageSeq] || articleImages['article-ucl']}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-800
                  [&_h2]:font-heading [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4
                  [&_h3]:font-heading [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-foreground-900 [&_h3]:mt-6 [&_h3]:mb-3
                  [&_p]:leading-relaxed [&_p]:mb-4 [&_p]:text-foreground-700
                  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4 [&_li]:mb-1 [&_li]:text-foreground-700
                  [&_strong]:text-foreground-900 [&_strong]:font-semibold"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 pt-8 border-t border-background-200">
                <p className="text-xs text-foreground-400">
                  声明：本文内容仅供参考，不构成任何建议。请理性参与体育娱乐活动。18岁以下未成年人禁止参与。
                </p>
              </div>
            </article>

            {relatedArticles.length > 0 && (
              <div className="mt-14">
                <h3 className="font-heading text-xl font-bold text-foreground-950 mb-6">相关文章</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="group bg-background-50 border border-background-200 rounded-lg p-4 hover:border-primary-300 transition-all duration-200 cursor-pointer"
                    >
                      <span className="text-xs text-accent-600 font-medium">{ra.category}</span>
                      <h4 className="font-semibold text-foreground-900 text-sm mt-1 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h4>
                      <div className="flex items-center gap-3 text-xs text-foreground-400">
                        <span>{ra.date}</span>
                        <span>{ra.readTime}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}